import React from "react";
import { Button } from "@/shared/components/ui/button";
import { Plus, Info, X } from "lucide-react";
import PeopleTable from "./PeopleTable";
import { AttorneyBanner } from "./AttorneyBanner";
import { Input } from "@/shared/components/ui/input";
import { useUserCustomizations } from "@/shared/hooks/useUserCustomizations";

import type { PersonContact } from "@/shared/types/estateContact";
import { IconButton } from "@/shared/components/ui/icon-button";
import { isProfessionalContact, isAttorneyContact, getBannerDismissKey } from "./contactUtils";

interface PeopleListContainerProps {
  contacts: PersonContact[];
  allContacts: PersonContact[];
  isLoading: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  deceasedUserData?: {
    firstName?: string;
    displayName?: string;
    surName?: string;
  } | null;
  onInviteContact: (contact: PersonContact) => Promise<void>;
  onResendInvitation: (contact: PersonContact) => Promise<void>;
  onCancelInvitation?: (invitationId: number) => Promise<void>;
  pendingInvitations?: Array<{
    id: number;
    email: string;
    invitation_token: string;
    role: string;
    expires_at: string;
    created_at: string;
  }>;
  acceptedInvitations?: Array<{
    id: number;
    email: string;
    invitation_token: string;
    role: string;
    expires_at: string;
    created_at: string;
    updated_at?: string;
  }>;
  currentUserEmail?: string;
  accountOwner?: {
    user_id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
  } | null;
  resendingInvitationEmail?: string | null;
  isSendingInvitation?: boolean;
  onRemoveAccess?: (email: string) => Promise<void>;
  onAddContact: () => void;
  onAddAttorney: () => void;
  isSimulating?: boolean;
}

const PeopleListContainer: React.FC<PeopleListContainerProps> = ({
  contacts,
  allContacts,
  isLoading,
  searchQuery,
  onSearchChange,
  deceasedUserData,
  onInviteContact,
  onResendInvitation,
  onCancelInvitation,
  pendingInvitations,
  acceptedInvitations,
  currentUserEmail,
  accountOwner,
  resendingInvitationEmail,
  isSendingInvitation = false,
  onRemoveAccess,
  onAddContact,
  onAddAttorney,
  isSimulating = false,
}) => {
  const {
    getCustomization,
    setCustomization,
    isFetched: isCustomizationsFetched,
  } = useUserCustomizations();

  const extractedContactsCount = allContacts.filter(
    (contact) => contact.extracted_from_document,
  ).length;

  const hasAttorney = allContacts.some((contact) => isAttorneyContact(contact));
  const currentEmailLower = currentUserEmail?.toLowerCase();
  const hasFamilyContacts = allContacts.some(
    (contact) =>
      !isProfessionalContact(contact) && contact.email?.toLowerCase() !== currentEmailLower,
  );

  const bannerKey = getBannerDismissKey(hasAttorney);
  const showBanner =
    !isLoading &&
    isCustomizationsFetched &&
    !getCustomization(bannerKey, false) &&
    !(hasAttorney && hasFamilyContacts);

  return (
    <div className="flex flex-col gap-6">
      {showBanner && (
        <AttorneyBanner
          onAddAttorney={onAddAttorney}
          onAddContact={onAddContact}
          hasAttorney={hasAttorney}
          disabled={isSimulating}
        />
      )}

      <div className="w-full h-[40px] flex items-center gap-2 md:gap-4">
        <div className="flex-1 min-w-0">
          <Input
            variant="search"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        <Button
          variant="brand"
          size="sm"
          className="h-[40px] w-[40px] md:w-auto flex items-center justify-center gap-0 md:gap-2 p-0 md:px-4 flex-shrink-0"
          onClick={onAddContact}
          disabled={isSimulating}
          title={isSimulating ? "View-only mode during simulation" : undefined}
          aria-label="Add contact"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden md:inline whitespace-nowrap">Add Contact</span>
        </Button>
      </div>

      {extractedContactsCount > 0 && !getCustomization("hide_contacts_banner", false) && (
        <div className="w-full bg-white rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-secondary flex-shrink-0" />
            <p className="text-sm text-secondary flex-1">
              {extractedContactsCount} contact
              {extractedContactsCount !== 1 ? "s were" : " was"} automatically added from your
              uploaded documents
            </p>
            <IconButton
              onClick={() => setCustomization("hide_contacts_banner", true)}
              label="Close banner"
              tooltip={false}
            >
              <X />
            </IconButton>
          </div>
        </div>
      )}

      <PeopleTable
        contacts={contacts}
        isLoading={isLoading}
        searchQuery={searchQuery}
        deceasedUserData={deceasedUserData}
        onInviteContact={onInviteContact}
        onResendInvitation={onResendInvitation}
        onCancelInvitation={onCancelInvitation}
        pendingInvitations={pendingInvitations}
        acceptedInvitations={acceptedInvitations}
        currentUserEmail={currentUserEmail}
        accountOwner={accountOwner}
        resendingInvitationEmail={resendingInvitationEmail}
        isSendingInvitation={isSendingInvitation}
        isSimulating={isSimulating}
        onRemoveAccess={onRemoveAccess}
      />
    </div>
  );
};

export default PeopleListContainer;
